import React, { useState, useEffect, useRef } from "react";
import { Search, Plus } from "lucide-react";
import api from "../lib/api";
import { fmtDate } from "../lib/format";

/** Inline typeahead that searches a customer's existing equipment by part /
 *  serial / asset (prefix match). Click a row to fire `onPick(match)`, or
 *  click "Create new" to dismiss with `onCreateNew()`. Use for the Receiving
 *  form line items.
 */
export function EquipmentTypeahead({
  customerId,
  field,           // "serial_number" | "part_number" | "asset_number" | "description"
  value,
  placeholder,
  onChange,
  onPick,
  onCreateNew,
  onCrossCustomerMatch,   // (matches[]) — fired when field="serial_number" resolves to another customer's record
  minimal,         // SPT-PTE-2026-0045: show only the matched field's value, no extra info
  testid,
}) {
  const [matches, setMatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!customerId || !value || value.length < 2) { setMatches([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get("/equipment/lookup", {
          params: { customer_id: customerId, [field]: value, prefix: true },
        });
        setMatches(r.data.matches || []);
      } catch { setMatches([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [customerId, field, value]);

  // Cross-customer duplicate guard — serial numbers only (asset/part numbers
  // legitimately repeat across different customers' own tagging schemes, a
  // serial number should not). Separate debounced call, exact match, fires
  // regardless of whether the same-customer search above found anything.
  useEffect(() => {
    if (field !== "serial_number" || !onCrossCustomerMatch) return;
    if (!customerId || !value || value.trim().length < 3) { onCrossCustomerMatch([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/equipment/duplicate-check", {
          params: { customer_id: customerId, serial_number: value },
        });
        onCrossCustomerMatch(r.data.matches || []);
      } catch { onCrossCustomerMatch([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [customerId, field, value]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const close = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const hasResults = open && (loading || matches.length > 0 || (value && value.length >= 2));

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div className="input" style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px" }}>
        <Search size={13} color="var(--text-3)" />
        <input
          className="mono"
          style={{ border: "none", outline: "none", width: "100%", background: "none", fontSize: 13 }}
          placeholder={placeholder}
          value={value || ""}
          onChange={(e) => { onChange(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          data-testid={testid}
        />
      </div>
      {hasResults && (
        <div className="card" style={{ position: "absolute", zIndex: 20, top: 40, left: 0, right: 0, maxHeight: 260, overflowY: "auto", padding: 4 }}>
          {loading && <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>Searching…</div>}
          {!loading && matches.length === 0 && value && value.length >= 2 && (
            <div className="t-mut" style={{ fontSize: 12, padding: "8px 10px" }}>No existing record matches &quot;{value}&quot;.</div>
          )}
          {matches.map((m) => (
            <button key={m.id} type="button" className="nav-item" style={{ color: "var(--text)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px" }} onClick={() => { onPick(m); setOpen(false); }} data-testid={`${testid}-opt-${m.id}`}>
              {minimal ? (
                // SPT-PTE-2026-0045: Description/Part number dropdowns should show
                // just the matched value — no manufacturer/SN/history clutter.
                <div className="t-strong" style={{ fontSize: 13 }}>{m[field] || m.description || "—"}</div>
              ) : (
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "baseline" }}>
                  <div>
                    <div className="t-strong" style={{ fontSize: 13 }}>{m.description || "—"}</div>
                    <div className="s mono" style={{ fontSize: 11 }}>
                      {/* SPT-PTE-2026-0022: SN is redundant/cluttering when searching BY part
                          number (every past serial linked to that PN shows here) — only
                          surface it when the search itself is by serial number. */}
                      {field !== "part_number" && <>SN {m.serial_number || "—"} · </>}
                      PN {m.part_number || "—"} · Issue {m.issue_number || "—"}
                    </div>
                  </div>
                  <div className="mono" style={{ fontSize: 11, color: "var(--text-3)", whiteSpace: "nowrap" }}>
                    {m.calibration_history_count || 0}× · last {fmtDate(m.last_calibration_date)}
                  </div>
                </div>
              )}
            </button>
          ))}
          {onCreateNew && value && value.length >= 2 && (
            <button type="button" className="nav-item" style={{ color: "var(--gold)", borderRadius: 6, width: "100%", textAlign: "left", padding: "8px 10px", borderTop: "1px solid var(--line)", display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600 }} onClick={() => { onCreateNew(); setOpen(false); }} data-testid={`${testid}-create-new`}>
              <Plus size={13} /> Add as new equipment (&quot;{value}&quot;)
            </button>
          )}
        </div>
      )}
    </div>
  );
}
